import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useFormDraft } from '@/hooks/useFormDraft';
import { useDirtyGuard } from '@/hooks/useDirtyGuard';
import { useAppSelector } from '@/features/hooks';
import { DiscardChangesDialog } from '@/components/common/DiscardChangesDialog';
import { DraftRestoredBanner } from '@/components/common/DraftRestoredBanner';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  RequiredAsterisk,
  OptionalTag,
} from '@/components/ui/form';
import { FieldTooltip } from '@/components/ui/field-tooltip';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { getCurrencySymbol, getCurrencyStep, getCurrencyMinAmount } from '@/lib/utils';
import { EmojiPicker } from '@/components/ui/emoji-picker';
import { ColorPicker } from '@/components/ui/color-picker';
import { useCurrency } from '@/hooks/useCurrency';
import type { Goal } from '@/lib/types';

const goalSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100),
  type: z.enum(['savings', 'debt_payoff', 'investment', 'other']),
  targetAmount: z.coerce.number().positive('Target amount must be positive'),
  currentAmount: z.coerce.number().min(0).optional(),
  targetDate: z.date().optional(),
  priority: z.coerce.number().int().min(1).max(10),
  status: z.enum(['active', 'completed', 'paused']),
  notes: z.string().max(500).optional(),
  emoji: z.string().optional(),
  color: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/i, 'Use a hex color like #10b981')
    .default('#10b981'),
});

export type GoalFormData = z.infer<typeof goalSchema>;

interface GoalFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: GoalFormData) => Promise<void>;
  goal?: Goal | null;
  isLoading?: boolean;
}

export default function GoalForm({
  open,
  onOpenChange,
  onSubmit,
  goal,
  isLoading = false,
}: GoalFormProps) {
  const isEditing = !!goal;
  const { currency } = useCurrency();
  const userId = useAppSelector((state) => state.auth.user?.id);
  const currencySymbol = getCurrencySymbol(currency);
  const step = getCurrencyStep(currency);
  const minAmount = getCurrencyMinAmount(currency);

  const form = useForm<GoalFormData>({
    resolver: zodResolver(goalSchema),
    defaultValues: {
      name: '',
      type: 'savings',
      targetAmount: 0,
      currentAmount: 0,
      priority: 5,
      status: 'active',
      notes: '',
      emoji: '',
      color: '#10b981',
    },
  });

  const { clearDraft, saveDraftNow, justRestored, restoredAge, dismissRestore } = useFormDraft<GoalFormData>(
    'goal-create',
    userId,
    form,
    !isEditing && open
  );

  const { guardedOnOpenChange, showDiscard, confirmDiscard, cancelDiscard, saveDraftAndClose } =
    useDirtyGuard(form, onOpenChange, clearDraft, saveDraftNow);

  useEffect(() => {
    if (goal) {
      form.reset({
        name: goal.name,
        type: goal.type,
        targetAmount: goal.targetAmount,
        currentAmount: goal.currentAmount,
        targetDate: goal.targetDate ? new Date(goal.targetDate) : undefined,
        priority: goal.priority,
        status: goal.status,
        notes: goal.notes || '',
        emoji: goal.emoji || '',
        color: goal.color || '#10b981',
      });
    } else {
      form.reset({
        name: '',
        type: 'savings',
        targetAmount: 0,
        currentAmount: 0,
        priority: 5,
        status: 'active',
        notes: '',
        emoji: '',
        color: '#10b981',
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goal, open]);

  const handleSubmit = async (data: GoalFormData) => {
    await onSubmit(data);
    clearDraft();
    form.reset();
  };

  return (
    <>
    <Sheet open={open} onOpenChange={guardedOnOpenChange}>
      <SheetContent side="right" className="sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{isEditing ? 'Edit Goal' : 'Create Goal'}</SheetTitle>
          <SheetDescription>
            {isEditing
              ? 'Update your financial goal.'
              : 'Set a new financial goal to work towards.'}
          </SheetDescription>
        </SheetHeader>

        {justRestored && (
          <DraftRestoredBanner
            age={restoredAge}
            onDismiss={dismissRestore}
            className="mx-0 mt-4"
          />
        )}

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            {/* Name */}
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Goal Name<RequiredAsterisk /><FieldTooltip text="A short, motivating name for your goal, e.g., 'Emergency Fund', 'Japan Trip 2026', 'Debt-Free by 30'." /></FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., Emergency Fund" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Emoji */}
            <FormField
              control={form.control}
              name="emoji"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Emoji<OptionalTag /><FieldTooltip text="A visual tag for quick scanning. Purely decorative." /></FormLabel>
                  <FormControl>
                    <EmojiPicker value={field.value} onChange={field.onChange} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Color */}
            <FormField
              control={form.control}
              name="color"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Color<OptionalTag /><FieldTooltip text="Assign a color to make this goal stand out in lists and charts." /></FormLabel>
                  <FormControl>
                    <ColorPicker value={field.value} onChange={field.onChange} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Type & Priority */}
            <div className="grid grid-cols-2 gap-3">
              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Type<RequiredAsterisk /><FieldTooltip text="Savings = building up money. Debt Payoff = reducing what you owe. Investment = growing wealth." /></FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="savings">Savings</SelectItem>
                        <SelectItem value="debt_payoff">Debt Payoff</SelectItem>
                        <SelectItem value="investment">Investment</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="priority"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Priority<OptionalTag /><FieldTooltip text="1 = highest priority, 10 = lowest. Helps you stay focused when managing multiple goals." /></FormLabel>
                    <FormControl>
                      <Input type="number" min={1} max={10} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Target Amount */}
            <FormField
              control={form.control}
              name="targetAmount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Target Amount ({currencySymbol})<RequiredAsterisk /><FieldTooltip text="How much money you want to reach. For debt payoff, enter the total debt balance." /></FormLabel>
                  <FormControl>
                    <Input type="number" step={step} min={minAmount} placeholder="0" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Current Amount (for create or editing) */}
            <FormField
              control={form.control}
              name="currentAmount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Current Amount ({currencySymbol})<OptionalTag /><FieldTooltip text="How much you've already saved or paid off toward this goal. Leave at 0 if starting fresh." /></FormLabel>
                  <FormControl>
                    <Input type="number" step={step} min="0" placeholder="0" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Target Date */}
            <FormField
              control={form.control}
              name="targetDate"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Target Date<OptionalTag /><FieldTooltip text="Optional deadline. Used to calculate required monthly contributions to stay on track." /></FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant="outline"
                          className={cn(
                            'w-full pl-3 text-left font-normal',
                            !field.value && 'text-muted-foreground'
                          )}
                        >
                          {field.value ? format(field.value, 'PPP') : 'Pick a target date'}
                          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={field.value}
                        onSelect={field.onChange}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Status (only when editing) */}
            {isEditing && (
              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status<OptionalTag /><FieldTooltip text="Active = working on it. Paused = temporarily stopped. Completed = goal reached." /></FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="paused">Paused</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* Notes */}
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes<OptionalTag /><FieldTooltip text="Personal notes for your records only — context, reminders, links." /></FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Any details about this goal..."
                      className="resize-none"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <SheetFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => guardedOnOpenChange(false)}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isEditing ? 'Update' : 'Create'}
              </Button>
            </SheetFooter>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
      <DiscardChangesDialog
        open={showDiscard}
        onConfirm={confirmDiscard}
        onCancel={cancelDiscard}
        onSaveDraft={!isEditing ? saveDraftAndClose : undefined}
      />
    </>
  );
}
